
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mic, Radio, Share2, StopCircle, Volume2, Users, ArrowLeft, Loader2, Play } from 'lucide-react';
import { BACKEND_URL } from '@/App';

function MKLive() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { channelId } = useParams(); // Optional: if provided, auto-join as listener
  
  // State
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, connecting, live, error
  
  // Refs
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const queueRef = useRef([]);
  const isSourceOpenRef = useRef(false);
  
  // Hardcoded channel for "General" if not specified
  const activeChannel = channelId || 'general';
  
  // Roles logic - Broadcaster needs auth, Listener does NOT
  const isCoach = user?.role === 'entrenador';
  const isPro = user?.es_pro;
  const canBroadcast = isCoach || isPro;

  // Auto-join logic for listeners with link
  useEffect(() => {
    if (channelId && !canBroadcast) {
        startListening();
    }
  }, [channelId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopBroadcast();
      stopListening();
    };
  }, []);

  // ================= BROADCASTER LOGIC =================
  const startBroadcast = async () => {
    try {
      setStatus('connecting');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Connect WS
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host.replace('3000', '8001')}/ws/live/${activeChannel}`;
      // In production/preview, backend might be on same domain/port via proxy, or different.
      // Adjusting for Emergent env:
      // Frontend: xxxx.preview.emergentagent.com
      // Backend: xxxx.preview.emergentagent.com/api... but WS is usually root /ws
      // Let's try relative path if proxy is set up, otherwise full URL.
      // Based on previous issues, use REACT_APP_BACKEND_URL but replace http with ws
      const backendUrl = BACKEND_URL.replace(/^http/, 'ws');
      const finalWsUrl = `${backendUrl}/ws/live/${activeChannel}`;
      
      const ws = new WebSocket(finalWsUrl);
      
      ws.onopen = () => {
        console.log('WS Connected');
        setStatus('live');
        setIsBroadcasting(true);
        
        // Start Recorder
        // TimeSlice 100ms for low latency
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            ws.send(event.data);
          }
        };
        mediaRecorder.start(100);
        mediaRecorderRef.current = mediaRecorder;
      };
      
      ws.onerror = (e) => {
        console.error('WS Error', e);
        setStatus('error');
      };
      
      wsRef.current = ws;
      
    } catch (err) {
      console.error(err);
      alert('Error al acceder al micrófono: ' + err.message);
      setStatus('error');
    }
  };

  const stopBroadcast = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsBroadcasting(false);
    setStatus('idle');
  };

  // ================= LISTENER LOGIC (MSE) =================
  const startListening = () => {
    try {
      setStatus('connecting');
      
      // Setup Audio Element and MediaSource
      const audio = document.getElementById('audio-player');
      const mediaSource = new MediaSource();
      audio.src = URL.createObjectURL(mediaSource);
      
      mediaSource.addEventListener('sourceopen', () => {
        isSourceOpenRef.current = true;
        try {
            const sourceBuffer = mediaSource.addSourceBuffer('audio/webm;codecs=opus');
            sourceBuffer.mode = 'sequence';
            sourceBufferRef.current = sourceBuffer;
            
            sourceBuffer.addEventListener('updateend', () => {
                if (queueRef.current.length > 0 && !sourceBuffer.updating) {
                    sourceBuffer.appendBuffer(queueRef.current.shift());
                }
            });
        } catch (e) {
            console.error('MSE Error:', e);
            alert('Tu navegador no soporta este formato de audio.');
        }
      });

      // Connect WS
      const backendUrl = BACKEND_URL.replace(/^http/, 'ws');
      const finalWsUrl = `${backendUrl}/ws/live/${activeChannel}`;
      const ws = new WebSocket(finalWsUrl);
      ws.binaryType = 'arraybuffer';
      
      ws.onopen = () => {
        console.log('WS Listener Connected');
        setStatus('live');
        setIsListening(true);
        audio.play().catch(e => console.log('Autoplay blocked:', e));
      };
      
      ws.onmessage = async (event) => {
        const data = event.data;
        if (sourceBufferRef.current && !sourceBufferRef.current.updating) {
            try {
                sourceBufferRef.current.appendBuffer(data);
            } catch (e) {
                // If buffer is full or error, just log
                console.error(e);
            }
        } else {
            queueRef.current.push(data);
        }
      };
      
      ws.onerror = (e) => setStatus('error');
      ws.onclose = () => {
        setStatus('idle');
        setIsListening(false);
      };
      
      wsRef.current = ws;
      
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const stopListening = () => {
    if (wsRef.current) wsRef.current.close();
    setIsListening(false);
    setStatus('idle');
    isSourceOpenRef.current = false;
  };

  const shareLink = () => {
    const url = `${window.location.origin}/mk-live/${activeChannel}`;
    if (navigator.share) {
      navigator.share({
        title: 'MK Live',
        text: 'Únete a la transmisión en vivo de Mallorca Klan',
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Enlace copiado al portapapeles');
    }
  };

  return (
    <div className="min-h-screen bg-dark-mk p-4 flex flex-col items-center">
      <div className="w-full max-w-md space-y-6 mt-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white p-0"
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${status === 'live' ? 'bg-red-500 animate-pulse' : 'bg-gray-600'}`}></div>
            <span className="text-gold font-bold uppercase tracking-wider">MK Live</span>
          </div>
          <div className="w-6"></div> {/* Spacer */}
        </div>

        {/* Main Card */}
        <Card className="bg-gray-900 border-gray-800 shadow-2xl relative overflow-hidden">
            {status === 'live' && (
                <div className="absolute inset-0 bg-red-900/10 pointer-events-none animate-pulse"></div>
            )}
            
            <CardContent className="p-8 flex flex-col items-center text-center space-y-8 relative z-10">
                
                {/* Visualizer / Icon */}
                <div className={`w-40 h-40 rounded-full flex items-center justify-center border-4 transition-all duration-500
                    ${status === 'live' ? 'border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.4)]' : 'border-gray-700'}
                `}>
                    {isBroadcasting ? (
                        <Mic className={`w-16 h-16 ${status === 'live' ? 'text-white' : 'text-gray-500'}`} />
                    ) : (
                        <Radio className={`w-16 h-16 ${status === 'live' ? 'text-white' : 'text-gray-500'}`} />
                    )}
                </div>

                {/* Status Text */}
                <div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                        {status === 'live' ? (isBroadcasting ? 'Transmitiendo' : 'En Vivo') : 'Desconectado'}
                    </h2>
                    <p className="text-gray-400 text-sm">
                        {status === 'live' 
                            ? 'Canal General' 
                            : 'Pulsa el botón para comenzar'}
                    </p>
                </div>

                {/* Controls */}
                <div className="w-full space-y-4">
                    {/* BROADCASTER CONTROLS */}
                    {canBroadcast && !isListening && (
                        !isBroadcasting ? (
                            <Button 
                                onClick={startBroadcast}
                                className="w-full py-6 text-lg font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl transition-transform active:scale-95 shadow-lg"
                                disabled={status === 'connecting'}
                            >
                                {status === 'connecting' ? <Loader2 className="animate-spin mr-2" /> : <Mic className="mr-2" />}
                                Iniciar Transmisión
                            </Button>
                        ) : (
                            <div className="space-y-4">
                                <div className="text-red-400 animate-pulse font-mono">LIVE 00:00</div>
                                <Button 
                                    onClick={stopBroadcast}
                                    variant="outline"
                                    className="w-full border-red-500 text-red-500 hover:bg-red-900/20"
                                >
                                    <StopCircle className="mr-2" /> Detener
                                </Button>
                                <Button 
                                    onClick={shareLink}
                                    className="w-full btn-primary-mk"
                                >
                                    <Share2 className="mr-2 h-4 w-4" /> Compartir Enlace
                                </Button>
                            </div>
                        )
                    )}

                    {/* LISTENER CONTROLS */}
                    {(!isBroadcasting) && (
                        !isListening ? (
                            <Button 
                                onClick={startListening}
                                className="w-full py-6 text-lg font-bold btn-primary-mk rounded-xl shadow-lg"
                                disabled={status === 'connecting'}
                            >
                                {status === 'connecting' ? <Loader2 className="animate-spin mr-2" /> : <Play className="mr-2 fill-current" />}
                                Escuchar Transmisión
                            </Button>
                        ) : (
                            <Button 
                                onClick={stopListening}
                                variant="secondary"
                                className="w-full bg-gray-800 text-white hover:bg-gray-700"
                            >
                                <StopCircle className="mr-2" /> Dejar de escuchar
                            </Button>
                        )
                    )}
                </div>

                {/* Audio Element (Hidden) */}
                <audio id="audio-player" style={{ display: 'none' }} controls></audio>

            </CardContent>
        </Card>

        {/* Info Box */}
        <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-400 flex gap-3">
            <Users className="h-5 w-5 shrink-0 text-gold" />
            <p>
                <b>MK Live:</b> Transmite o escucha los entrenos en tiempo real a través de internet. 
                Ideal si el Bluetooth no llega o hay mucha gente.
            </p>
        </div>
      </div>
    </div>
  );
}

export default MKLive;
